import React, { useState } from 'react';
import {
  TableCell, TableRow, Collapse, IconButton, Box, Typography,
  Chip, MuiLink, Tooltip, ListItemIcon, ListItemText,
} from 'components/mui';
import { Checkbox, Menu, MenuItem } from '@mui/material';
import { ExpandMore, MoreVertIcon, EditIcon, CopyIcon } from 'components/icons';
import { useNavigate } from 'react-router-dom';
import { ROUTE_CUSTOMERS } from 'constants/routerPath';
import { Case } from 'containers/pages/cases/types';
import { getStatusColor } from 'containers/pages/cases/utils/getStatusColor';
import CaseListRowDetail from './CaseListRowDetail';

interface CaseListRowProps {
  caseItem: Case;
  isOpen: boolean;
  onToggle: () => void;
  onCaseClick?: (caseId: number) => void;
  totalCols: number;
  selected?: boolean;
  onSelect?: (id: number) => void;
  onEdit?: (caseId: number) => void;
  onDuplicate?: (caseId: number) => void;
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  try {
    const dotNetMatch = /\/Date\((-?\d+)\)\//.exec(dateStr);
    const d = dotNetMatch
      ? new Date(parseInt(dotNetMatch[1], 10))
      : new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return dateStr; }
};

function CaseListRow({ caseItem, isOpen, onToggle, onCaseClick, totalCols, selected, onSelect, onEdit, onDuplicate }: CaseListRowProps) {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  return (
    <React.Fragment>
      <TableRow hover onClick={onToggle} sx={{ cursor: 'pointer', '&.MuiTableRow-hover:hover': { bgcolor: 'grey.100' }, '&:hover .row-actions': { opacity: 1 }, ...(selected ? { bgcolor: 'action.selected' } : {}) }}>
        {onSelect && (
          <TableCell padding="checkbox">
            <Checkbox size="small" checked={!!selected} onClick={(e) => e.stopPropagation()} onChange={() => onSelect(caseItem.Id)} />
          </TableCell>
        )}
        <TableCell sx={{ width: 40, px: 0.5 }}>
          <IconButton aria-label={isOpen ? 'Collapse' : 'Expand'} aria-expanded={isOpen} size="small">
            <ExpandMore sx={{ fontSize: 18, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </IconButton>
        </TableCell>
        <TableCell>
          <MuiLink component="button" variant="body2"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); onCaseClick?.(caseItem.Id); }}
            sx={{ cursor: 'pointer', textDecoration: 'none', fontWeight: 500, color: 'text.primary', '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
          >
            {caseItem.Title}
          </MuiLink>
        </TableCell>
        <TableCell>
          {caseItem.CustomerId ? (
            <MuiLink component="button" variant="body2"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate(`${ROUTE_CUSTOMERS}/${caseItem.CustomerId}`); }}
              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'text.secondary', '&:hover': { textDecoration: 'underline', color: 'primary.main' } }}
            >
              {caseItem.CustomerName}
            </MuiLink>
          ) : (
            <Typography variant="body2" color="text.secondary">{caseItem.CustomerName}</Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip label={caseItem.StatusTitle || 'N/A'} size="small"
            color={getStatusColor(caseItem.StatusTitle)}
            sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: 1 }}
          />
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
          <Typography variant="body2" color="text.secondary" noWrap>{caseItem.ConsultantName}</Typography>
        </TableCell>
        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
          <Typography variant="caption" color="text.disabled" noWrap>{formatDate(caseItem.UpdatedOn)}</Typography>
        </TableCell>
        {/* Actions menu - hidden for now
        <TableCell sx={{ width: 48, px: 0.5 }}>
          <Box className="row-actions" sx={{ opacity: { xs: 1, md: 0 }, transition: 'opacity 0.15s' }}>
            <Tooltip title="Actions">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}>
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
            onClick={(e) => e.stopPropagation()} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => { setMenuAnchor(null); onCaseClick?.(caseItem.Id); }}>
              <ListItemText>View</ListItemText>
            </MenuItem>
            {onEdit && (
              <MenuItem onClick={() => { setMenuAnchor(null); onEdit(caseItem.Id); }}>
                <ListItemIcon><EditIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
            )}
            {onDuplicate && (
              <MenuItem onClick={() => { setMenuAnchor(null); onDuplicate(caseItem.Id); }}>
                <ListItemIcon><CopyIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                <ListItemText>Duplicate</ListItemText>
              </MenuItem>
            )}
          </Menu>
        </TableCell> */}
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={totalCols}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <CaseListRowDetail caseItem={caseItem} formatDate={formatDate} />
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default CaseListRow;
